import axios, {AxiosError, AxiosPromise, AxiosResponse} from "axios";
import {IApiResponse, Protocol, RequestType} from "../Base/Api";
import {ApiProxy, IApiProxy} from "./ApiProxy";
import {number} from "prop-types";
import {StorageHelper} from "./StorageHelper";


class ApiRequestInternal {
    apiPath: string;
    requestProxy: IApiProxy | undefined;
    networkExecutor: any;

    config = {
        headers: {
        },
        withCredentials: true
    };

    constructor(hostName: string, port: number = 80, postfix = "", protocol: Protocol = Protocol.Auto) {
        const portPart = port == 80 ? "" : ":" + port;
        //const protocolPath = protocol == Protocol.None ? "" : protocol + "://";
        let protocolPath = "";
        if(protocol == Protocol.Auto) {
            const href = window.location.href;
            protocolPath = href.substring(0, href.indexOf("://"));
        }

        if(protocol != Protocol.None) {
            protocolPath = protocolPath + "://";
        }

        this.apiPath =  protocolPath + hostName + portPart + postfix;
        this.requestProxy = undefined;
        this.networkExecutor = axios;
    }

    __getApiPath() {
        return this.apiPath;
    }

    get<T>(route: string) {
        return this._safeRequest<T>(RequestType.Get, route);
    }

    post<T>(route: string, data?: any) {
        return this._safeRequest<T>(RequestType.Post, route, data);
    }

    put<T>(route: string, data?: any) {
        return this._safeRequest<T>(RequestType.Put, route, data);
    }

    patch<T>(route: string, data?: any) {
        return this._safeRequest<T>(RequestType.Patch, route, data);
    }

    delete<T>(route: string, data?: any) {
        return this._safeRequest<T>(RequestType.Delete, route, data);
    }

    all(r: any[]) {
        return axios.all(r);
    }

    _safeRequest<T>(requestType: RequestType, route: string, data?: any): Promise<IApiResponse<T>> {

        let isMethodProxyable = false;
        if(this.requestProxy !== undefined) {
           isMethodProxyable = this.requestProxy.hasProxyFor(route, requestType);
        }

        let methodToCall = this.networkExecutor[requestType];
        if(isMethodProxyable) {
            methodToCall = (this.requestProxy as any)[requestType]!;
        }

        const finalRoute = this.apiPath + route;
        const paramArray: any[] = [finalRoute];

        const configNext = {...this.config};
        if(requestType != RequestType.Get && requestType != RequestType.Delete) {
            paramArray.push(data);
        }

        if(requestType == RequestType.Delete) {
            (configNext as any)["data"] = data;
        }
        paramArray.push(configNext);

        const resolvedProxy = (r: AxiosResponse<T>) => {
          return new Promise(resolver => {
              const resData = this._transformFromResolved<T>(r);
              return resolver(resData);
          });
        };

        const rejectedProxy = (r: AxiosError) => {
            return new Promise((_, rejector) => {
                const resData = this._transformFromRejected<T>(r);
                return rejector(resData);
            });
        };

        const safeReturnPromise = methodToCall.apply(this.requestProxy, paramArray).then(resolvedProxy)
                .catch(rejectedProxy);

        return safeReturnPromise;
    }

    _transformFromResolved<T>(response: AxiosResponse<T>) {
        const responseJson = response.data as any;
        const data = responseJson.data;
        const errors = responseJson.errors;
        const meta = responseJson.meta === undefined ? {} : responseJson.meta;
        meta.axios = response;

        const resData: IApiResponse<T> = {data, errors, meta};
        return resData;
    }

    _transformFromRejected<T>(r: AxiosError) {
        if(r.response === undefined) {
            return {
                errors: [{
                    id: 0,
                    title: "Internet connection issue",
                    details: r.message}
                    ]
            };
        }

        //Ah hackish way do instantly logout a user without a session
        if(r.response.status == 466) {
            StorageHelper.clearAuthKey();
            location.hash = "/login";
        }
        let response = r.response!;
        const responseJson = response.data as any;
        const data = responseJson.data;
        const errors = responseJson.errors;
        const meta = responseJson.meta === undefined ? {} : responseJson.meta;
        meta.axios = response;

        const resData: IApiResponse<T> = {data, errors, meta};
        return resData;
    }
}

declare const __Host__: string, __Port__: number, __Version__: string, __Protocol__: string;
export const ApiRequest = new ApiRequestInternal(__Host__, __Port__, "/api/" + __Version__,
    Protocol.Auto);
const apiProxy = new ApiProxy();
ApiRequest.requestProxy = apiProxy;